<script setup lang="ts">
import { lectureService } from '@/api/services/lecture-service'
import BaseVideoThumnail from '@/components/base/BaseVideoThumbnail.vue'
import { useIsMobile } from '@/composables/useMediaQuery'
import type { ApiCursorBody } from '@/types/api-response.types'
import type { Lecture } from '@/types/lecture.types'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'

const isMobile = useIsMobile()
const router = useRouter()

// 스크롤 감지를 위한 변수들
const isLoading = ref(false)
const observer = ref<IntersectionObserver | null>(null)
const loadMoreTrigger = ref<HTMLElement | null>(null)

// 찜한 강좌 데이터
const lectureCursorInfo = ref<ApiCursorBody<Lecture>>()
const likedClassList = ref<Lecture[]>([])

const hoveredItemId = ref<number | null>(null)

const handleMouseEnter = (itemId: number) => {
  if (isMobile.value) return
  hoveredItemId.value = itemId
}

const handleMouseLeave = () => {
  if (isMobile.value) return
  hoveredItemId.value = null
}

const handleItemClick = (item: Lecture) => {
  router.push(`/video/${item.idx}`)
}

const emptyText = computed(() => {
  return isMobile.value ? '찜한 강좌가 없습니다.' : '찜한 강좌가 없습니다.'
})

// 추가 강의 로드 함수
const loadMoreLectures = async () => {
  if (isLoading.value || !lectureCursorInfo.value?.hasNext) return

  isLoading.value = true
  try {
    const nextCursor = lectureCursorInfo.value?.nextCursor
    const lectureCursorList = await lectureService.getLectures({
      onlyBookmarked: true,
      cursor: nextCursor,
      size: 18,
    })

    likedClassList.value = [...likedClassList.value, ...lectureCursorList.data]
    lectureCursorInfo.value = lectureCursorList
  } catch (error) {
    console.error('강의 로드 중 오류 발생:', error)
  } finally {
    isLoading.value = false
  }
}

// Intersection Observer 설정
const setupIntersectionObserver = () => {
  observer.value = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        loadMoreLectures()
      }
    },
    { threshold: 0.1 },
  )

  if (loadMoreTrigger.value) {
    observer.value.observe(loadMoreTrigger.value)
  }
}

onMounted(async () => {
  const lectureCursorList = await lectureService.getLectures({
    onlyBookmarked: true,
    cursor: undefined,
    size: 18,
  })
  likedClassList.value = lectureCursorList.data
  lectureCursorInfo.value = lectureCursorList

  setupIntersectionObserver()
})

onUnmounted(() => {
  if (observer.value) {
    observer.value.disconnect()
  }
})
</script>

<template>
  <div class="liked-class-list">
    <BaseVideoThumnail
      class="list-item"
      v-for="item in likedClassList"
      :key="item.idx"
      :lecture="item"
      :show-level="isMobile ? true : false"
      :show-play-button="false"
      :is-free="item.free"
      @mouseenter="handleMouseEnter(item.idx)"
      @mouseleave="handleMouseLeave"
      @video-click="handleItemClick(item)"
    />

    <!-- 결과가 없는 경우 -->
    <div v-if="likedClassList.length === 0" class="empty-state">
      <div class="empty-state-container">
        <div class="empty-state-icon-image">
          <img src="@/assets/svgs/cloud.svg" alt="empty-state-icon" />
        </div>
        <div class="empty-state-icon-text">
          <div class="title-sm white" :class="{ 'body-md': isMobile }">{{ emptyText }}</div>
        </div>
      </div>
    </div>

    <!-- 로딩 트리거 요소 -->
    <div ref="loadMoreTrigger" class="load-more-trigger">
      <div v-if="isLoading" class="loading-spinner">로딩 중...</div>
    </div>
  </div>
</template>

<style scoped>
.liked-class-list {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 28px;
}

.list-item:hover {
  transform: scale(1.1);
  z-index: 100;
  border: 1px solid var(--tint-300);
  cursor: pointer;
}

.liked-class-list:has(.list-item:hover) .list-item:not(:hover) {
  opacity: 0.7;
  transition: opacity 0.1s ease;
}

.empty-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 40px 0;
  min-height: 720px;
  max-height: 720px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.empty-state-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.empty-state-icon-image {
  width: 80px;
  height: 80px;
}

.empty-state-icon-image img {
  width: 100%;
  height: 100%;
}

.load-more-trigger {
  grid-column: 1 / -1;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.loading-spinner {
  color: var(--gray-600);
}

@media (max-width: 1365px) {
  .liked-class-list {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-top: 0px;
  }

  .list-item:hover {
    transform: none;
    border: none;
  }

  .liked-class-list:has(.list-item:hover) .list-item:not(:hover) {
    opacity: 1;
    transition: none;
  }
}
</style>
